/* eslint-disable react/jsx-no-bind */

import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import { useQuery } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import cn from "classnames";
import Icon from "~/components/shared/Icon";
import Badge from "~/components/shared/Badge";
import FriendlyTime from "~/components/shared/FriendlyTime";
import { fetchRepositories } from "~/components/pipeline/New/ConnectGitPicker/fetchRepositories";
import { getLanguageColor } from "~/components/pipeline/New/ConnectGitPicker/util/languageColors";
import type {
  SCMServiceInfo,
  RepositoryInfo,
  ManualProviderType,
} from "~/components/pipeline/New/ConnectGitPicker/util/types";
import Combox from "~/components/shared/Combox";
import ComboxTrigger from "~/components/shared/Combox/ComboxTrigger";
import ComboxPopover from "~/components/shared/Combox/ComboxPopover";
import ComboxList from "~/components/shared/Combox/ComboxList";
import ComboxItem from "~/components/shared/Combox/ComboxItem";
import ComboxFilterInput from "~/components/shared/Combox/ComboxFilterInput";
import Spinner from "~/components/shared/Spinner";

import { useRepositoryFetchWarning } from "~/components/pipeline/New/ConnectGitPicker/useRepositoryFetchWarning";
import { RepositoryFetchWarning } from "~/components/pipeline/New/ConnectGitPicker/RepositoryFetchWarning";

interface RepositoryPickerProps {
  selectedService: SCMServiceInfo | null;
  selectedRepository: RepositoryInfo | null;
  onRepositorySelect: (repository: RepositoryInfo | null) => void;
  onAutoSelectRepository?: (
    repositories: RepositoryInfo[],
    serviceUuid: string,
    force?: boolean,
  ) => void;

  repositoryValue?: string | null;
  onRepositoryUrlChange?: (url: string) => void;
  selectedManualProvider?: ManualProviderType;
  onSwitchToManualEntry?: () => void;
  setComboboxOpen: (open: boolean) => void;
  comboboxOpen: boolean;
}

const RepositoryPickerComponent = ({
  selectedService,
  selectedRepository,
  onRepositorySelect,
  onAutoSelectRepository,
  repositoryValue,
  onRepositoryUrlChange,
  selectedManualProvider = "git",
  onSwitchToManualEntry,
  setComboboxOpen,
  comboboxOpen,
}: RepositoryPickerProps) => {
  const [manualUrl, setManualUrl] = useState(repositoryValue || "");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const lastAutoSelectedServiceRef = useRef<string | null>(null);
  const comboxTriggerRef = useRef<HTMLButtonElement>(null);

  // We debounce the user input to prevent excessive calls to fetch repositories
  // It's easier (and more efficient) than debouncing the actual fetch function
  // Using a ref ensures that the debounced function is stable across renders and can be
  // properly cleaned up
  const debouncedSetSearchRef = useRef(
    debounce((value: string) => {
      setDebouncedSearch(value);
    }, 300),
  );
  useEffect(() => {
    debouncedSetSearchRef.current(search);
  }, [search]);
  useEffect(() => {
    const debouncedFunction = debouncedSetSearchRef.current;
    return () => {
      debouncedFunction.cancel();
    };
  }, []);

  // Clear out search state when service changes
  useEffect(() => {
    setSearch("");
    setDebouncedSearch("");
    debouncedSetSearchRef.current.cancel();
  }, [selectedService?.uuid]);

  const {
    data: repositories = [],
    isLoading: loading,
    isError: error,
  } = useQuery({
    queryKey: ["repositories", selectedService?.uuid, debouncedSearch],
    // Using React Query's AbortSignal prevents race conditions and ensures only the
    // latest request's results are used.
    queryFn: ({ signal }) => {
      if (!selectedService) {
        return Promise.resolve([]);
      }
      return fetchRepositories(selectedService, debouncedSearch, signal);
    },
    enabled: Boolean(selectedService?.repositories_url),
    // Use pre-loaded repositories as initial data for empty search
    initialData: () => {
      if (!debouncedSearch && selectedService?.repositories) {
        return selectedService.repositories;
      }
      return undefined;
    },
    // Keep previous data while searching within the same service to prevent flashing.
    // When switching services, don't show stale data from the previous service.
    placeholderData: (previousData, previousQuery) => {
      const previousServiceUuid = previousQuery?.queryKey[1];
      const currentServiceUuid = selectedService?.uuid;

      // Only use previous data if we're still on the same service
      if (previousServiceUuid === currentServiceUuid) {
        return previousData;
      }

      // Else don't show stale data
      return undefined;
    },
  });

  const { showSlowWarning, showTimeoutWarning } = useRepositoryFetchWarning({
    loading,
    error,
    hasRepositories: repositories.length > 0,
    selectedService,
  });

  // Auto-select first repository on initial load
  useEffect(() => {
    if (
      selectedService &&
      repositories.length > 0 &&
      onAutoSelectRepository &&
      !selectedRepository &&
      !debouncedSearch // Only auto-select on initial load, not during search
    ) {
      const isNewService = lastAutoSelectedServiceRef.current !== selectedService.uuid;
      if (isNewService) {
        onAutoSelectRepository(repositories, selectedService.uuid, true);
        lastAutoSelectedServiceRef.current = selectedService.uuid;
      }
    }
  }, [repositories, selectedRepository, onAutoSelectRepository, debouncedSearch, selectedService]);

  // Update manual URL when repositoryValue changes
  useEffect(() => {
    if (repositoryValue !== undefined) {
      setManualUrl(repositoryValue || "");
    }
  }, [repositoryValue]);

  // Memoized manual URL handlers
  const handleManualUrlChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setManualUrl(event.target.value);
  }, []);

  const handleManualUrlBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onRepositoryUrlChange?.(event.target.value);
    },
    [onRepositoryUrlChange],
  );

  // Memoized placeholder text
  const placeholderText = useMemo(() => {
    switch (selectedManualProvider) {
      case "gitlab":
        return "git@gitlab.com:your/repo.git";
      case "bitbucket":
        return "git@bitbucket.org:your/repo.git";
      default:
        return "git@github.com:your/repo.git";
    }
  }, [selectedManualProvider]);

  // Memoized button text
  const buttonText = useMemo(() => {
    if (selectedRepository) {
      return selectedRepository.full_name.split("/").pop() || selectedRepository.full_name;
    }
    // Show loading only when actually fetching, not when switching
    if (selectedService && !selectedRepository) {
      // If we have repositories but no selection, show prompt to select
      if (repositories.length > 0) {
        return "Select a repository...";
      }
      // Only show loading if we're actually loading AND have no repositories
      if (loading && repositories.length === 0) {
        return error ? "Failed to load repositories" : <Spinner color={false} />;
      }
      // If no repositories and not loading
      if (repositories.length === 0 && !loading) {
        return error ? "Failed to load repositories" : "No repositories found";
      }
    }
    if (repositoryValue?.trim()) {
      // Extract repo name from URL
      try {
        const sshMatch = repositoryValue.match(/^[^@]+@[^:]+:([^#]+?)(?:\.git)?$/);
        if (sshMatch?.[1]) {
          return sshMatch[1];
        }

        const httpsMatch = repositoryValue.match(/^https?:\/\/[^/]+\/(.+?)(?:\.git)?(?:#.*)?$/);
        if (httpsMatch?.[1]) {
          return httpsMatch[1];
        }
      } catch (error) {
        // Ignore parsing errors
      }
      return repositoryValue;
    }
    // Fallback to ensure we always show something
    const result = "Select repository";
    return result;
  }, [selectedRepository, selectedService, repositories.length, loading, error, repositoryValue]);

  // Memoized repository icon function
  const getRepositoryIcon = useCallback((visibility: string) => {
    switch (visibility) {
      case "private":
        return "repo-private";
      case "internal":
        return "repo-internal";
      case "public":
        return "repo-public";
      default:
        return "repo-public";
    }
  }, []);

  // Empty state component
  const emptyStateContent = useMemo(() => {
    if (loading && repositories.length === 0) {
      return "Loading repositories...";
    }

    if (error) {
      return "Failed to load repositories. Please try again.";
    }

    if (repositories.length === 0 && selectedService) {
      return `No repositories found for ${selectedService.account_login}.`;
    }

    return "No repositories found";
  }, [loading, error, repositories.length, selectedService]);

  // Manual URL input for non-connected services
  if (!selectedService) {
    return (
      <div className="min-w-0 flex-auto">
        <div className="relative">
          <input
            type="text"
            className="input -my-px -ml-px h-[32px] pr-10"
            placeholder={placeholderText}
            name="project[repository]"
            id="project_repository"
            autoComplete="off"
            value={manualUrl}
            onChange={handleManualUrlChange}
            onBlur={handleManualUrlBlur}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <RepositoryFetchWarning
        type={showTimeoutWarning ? "timeout" : "slow"}
        open={showSlowWarning || showTimeoutWarning}
        onSwitchToManual={() => onSwitchToManualEntry?.()}
        anchorRef={comboxTriggerRef}
      />
      <Combox
        // Combox holds on to its internal state when switching between different
        // services, which includes the search term entered by the user. Using the
        // service in the key forces it to reset instead.
        key={selectedService?.uuid}
        value={selectedRepository?.html_url || ""}
        onValueChange={(value) => {
          const repository = repositories.find((repo) => repo.html_url === value);
          if (repository) {
            onRepositorySelect(repository);
            setComboboxOpen(false);
          }
        }}
        onSearchChange={selectedService?.uuid === "examples" ? undefined : setSearch}
        open={comboboxOpen}
        onOpenChange={setComboboxOpen}
        disabled={
          (!selectedService && !selectedRepository && !repositoryValue?.trim()) ||
          (loading && repositories.length === 0)
        }
      >
        <ComboxTrigger
          ref={comboxTriggerRef}
          disabled={
            (!selectedService && !selectedRepository && !repositoryValue?.trim()) ||
            (loading && repositories.length === 0)
          }
          className="w-full"
          data-testid="git-repository-picker-trigger"
          id="repository-select"
        >
          <div className="flex items-center gap-2">
            {selectedRepository && (
              <Icon
                icon={getRepositoryIcon(selectedRepository.visibility)}
                className="h-4 w-4 text-gray-800"
              />
            )}
            <span className="truncate">{buttonText}</span>
          </div>
        </ComboxTrigger>

        <ComboxPopover data-testid="git-repository-picker-content">
          <ComboxFilterInput />
          <ComboxList
            emptyState={emptyStateContent}
            filterItems={selectedService?.uuid === "examples"}
          >
            {repositories.map((repository) => (
              <ComboxItem value={repository.html_url} key={repository.html_url}>
                <div className="flex w-full min-w-0 flex-row items-center gap-2 rounded pl-1 pr-2">
                  <span title={repository.visibility + " repository"}>
                    <Icon
                      icon={getRepositoryIcon(repository.visibility)}
                      className="h-4 w-4 text-gray-800"
                    />
                  </span>

                  <div className="flex min-w-0 flex-1 flex-col justify-start gap-0 text-left">
                    <div className="flex w-full flex-row gap-1">
                      <h3 className="m-0 truncate p-0 text-sm font-medium">
                        {repository.full_name.split("/").pop()}
                      </h3>
                      {repository.fork && (
                        <Badge outline={true} className="flex items-center py-0 text-xs">
                          Fork
                        </Badge>
                      )}
                    </div>

                    {repository.description ? (
                      <p
                        className={cn([
                          "dark-gray m-0 truncate p-0 text-xs",
                          selectedRepository?.html_url === repository.html_url &&
                            "!text-purple-600/70",
                        ])}
                      >
                        {repository.description}
                      </p>
                    ) : (
                      <p
                        className={cn([
                          "dark-gray m-0 truncate p-0 text-xs italic text-gray-600",
                          selectedRepository?.html_url === repository.html_url &&
                            "!text-purple-600/70",
                        ])}
                      >
                        No description
                      </p>
                    )}
                  </div>

                  <div className="ml-auto flex w-fit max-w-32 flex-col items-end gap-0 text-right">
                    {repository.updated_at && (
                      <FriendlyTime
                        value={repository.updated_at}
                        format="relative"
                        className="dark-gray m-0 truncate p-0 text-right text-xs"
                        clickable={false}
                        toggleable={false}
                      />
                    )}
                    {repository.language ? (
                      <div className="flex items-center gap-1">
                        <div
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: getLanguageColor(repository.language) }}
                        />
                        <p className="dark-gray m-0 truncate p-0 text-right text-xs">
                          {repository.language}
                        </p>
                      </div>
                    ) : (
                      <p className="m-0 truncate p-0 text-right text-xs italic text-gray-600">
                        Not detected
                      </p>
                    )}
                  </div>
                </div>
              </ComboxItem>
            ))}
          </ComboxList>
          {selectedService?.manage_url && selectedService.uuid !== "examples" && (
            <div className="border-t border-gray-300 bg-gray-50 px-5 py-2">
              <span className="text-xs text-gray-700">
                You can manage your repositories in your{" "}
                <a
                  href={selectedService.manage_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-700 hover:underline"
                >
                  GitHub settings
                </a>
              </span>
            </div>
          )}
        </ComboxPopover>
      </Combox>
    </>
  );
};

export const RepositoryPicker = memo(RepositoryPickerComponent);
